import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import LoginPage from "./pages/LoginPage";
import CompanySelectPage from "./pages/CompanySelectPage";
import PortalPage from "./pages/PortalPage";
import FeriePage from "./pages/FeriePage";
import CalendarPage from "./pages/CalendarPage";
import BustePagaPage from "./pages/BustePagaPage";
import TrasfertePage from "./pages/TrasfertePage";
import ApprovalsPage from "./pages/ApprovalsPage";
import ReportPage from "./pages/ReportPage";
import AdminPage from "./pages/AdminPage";
import SmartWorkingPage from "./pages/SmartWorkingPage";
import StraordinariPage from "./pages/StraordinariPage";
import CongediPage from "./pages/CongediPage";
import RubricaPage from "./pages/RubricaPage";
import OrganigrammaPage from "./pages/OrganigrammaPage";
import ComunicazioniPage from "./pages/ComunicazioniPage";
import DocumentiPage from "./pages/DocumentiPage";
import CertificatiPage from "./pages/CertificatiPage";
import FormazionePage from "./pages/FormazionePage";
import WelfarePage from "./pages/WelfarePage";
import ProfiloPage from "./pages/ProfiloPage";
import PolicyPage from "./pages/PolicyPage";
import CartaIntestataPage from "./pages/CartaIntestataPage";
import CambioPasswordPage from "./pages/CambioPasswordPage";
import GuidaPage from "./pages/GuidaPage";
import NovitaPage from "./pages/NovitaPage";
import AnteprimaGate from "./pages/AnteprimaGate";
import ComingSoonPage from "./pages/ComingSoonPage";
import SuperadminLogin from "./pages/superadmin/SuperadminLogin";
import SuperadminDashboard from "./pages/superadmin/SuperadminDashboard";
import SessionExpiryModal from "./components/SessionExpiryModal";
import RealtimeBridge from "./components/RealtimeBridge";
import ApprovalAlerts from "./components/ApprovalAlerts";
import PlanBanner from "./components/PlanBanner";

const ADMIN_ONLY = ["admin"];
const APPROVERS = ["responsabile", "admin"];

const Loader = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#f0f2f5]">
    <div className="w-10 h-10 border-4 border-[#16345c] border-t-transparent rounded-full animate-spin" />
  </div>
);

const ProtectedRoute = ({ children, allow, internalOnly, blockEmployee }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/cambio-password" replace />;
  if (allow && !allow.includes(user.role)) return <Navigate to="/" replace />;
  if (internalOnly && user.is_external) return <Navigate to="/" replace />;
  if (blockEmployee && user.role === "dipendente") return <Navigate to="/" replace />;
  return children;
};

const RequireLogin = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const PublicOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (user) return <Navigate to="/" replace />;
  return children;
};

// Home pubblica: chi NON è loggato vede la pagina "in allestimento";
// gli utenti autenticati vedono il portale. Il sito completo resta su /anteprima.
const RootRoute = () => {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <ComingSoonPage />;
  if (user.must_change_password) return <Navigate to="/cambio-password" replace />;
  return <PortalPage />;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <SessionExpiryModal />
          <RealtimeBridge />
          <ApprovalAlerts />
          <PlanBanner />
          <Routes>
            <Route path="/anteprima" element={<AnteprimaGate />} />
            <Route path="/superadmin/login" element={<SuperadminLogin />} />
            <Route path="/superadmin/dashboard" element={<SuperadminDashboard />} />
            <Route path="/superadmin" element={<Navigate to="/superadmin/login" replace />} />
            <Route path="/seleziona-societa" element={<PublicOnly><CompanySelectPage /></PublicOnly>} />
            <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
            <Route path="/cambio-password" element={<RequireLogin><CambioPasswordPage /></RequireLogin>} />
            <Route path="/" element={<RootRoute />} />
            <Route path="/ferie" element={<ProtectedRoute internalOnly><FeriePage /></ProtectedRoute>} />
            <Route path="/trasferte" element={<ProtectedRoute internalOnly><TrasfertePage /></ProtectedRoute>} />
            <Route path="/presenze" element={<Navigate to="/" replace />} />
            <Route path="/calendario" element={<ProtectedRoute internalOnly><CalendarPage /></ProtectedRoute>} />
            <Route path="/buste-paga" element={<ProtectedRoute internalOnly><BustePagaPage /></ProtectedRoute>} />
            <Route path="/smart-working" element={<ProtectedRoute internalOnly blockEmployee><SmartWorkingPage /></ProtectedRoute>} />
            <Route path="/straordinari" element={<ProtectedRoute internalOnly blockEmployee><StraordinariPage /></ProtectedRoute>} />
            <Route path="/congedi" element={<ProtectedRoute internalOnly><CongediPage /></ProtectedRoute>} />
            <Route path="/rubrica" element={<ProtectedRoute><RubricaPage /></ProtectedRoute>} />
            <Route path="/organigramma" element={<ProtectedRoute><OrganigrammaPage /></ProtectedRoute>} />
            <Route path="/comunicazioni" element={<ProtectedRoute><ComunicazioniPage /></ProtectedRoute>} />
            <Route path="/documenti" element={<ProtectedRoute><DocumentiPage /></ProtectedRoute>} />
            <Route path="/certificati" element={<ProtectedRoute internalOnly><CertificatiPage /></ProtectedRoute>} />
            <Route path="/formazione" element={<ProtectedRoute internalOnly blockEmployee><FormazionePage /></ProtectedRoute>} />
            <Route path="/welfare" element={<ProtectedRoute><WelfarePage /></ProtectedRoute>} />
            <Route path="/guida" element={<ProtectedRoute><GuidaPage /></ProtectedRoute>} />
            <Route path="/novita" element={<ProtectedRoute><NovitaPage /></ProtectedRoute>} />
            <Route path="/profilo" element={<ProtectedRoute><ProfiloPage /></ProtectedRoute>} />
            <Route path="/policy" element={<ProtectedRoute allow={ADMIN_ONLY}><PolicyPage /></ProtectedRoute>} />
            <Route path="/carta-intestata" element={<ProtectedRoute allow={ADMIN_ONLY}><CartaIntestataPage /></ProtectedRoute>} />
            <Route path="/approvazioni" element={<ProtectedRoute allow={APPROVERS}><ApprovalsPage /></ProtectedRoute>} />
            <Route path="/report" element={<ProtectedRoute allow={ADMIN_ONLY}><ReportPage /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute allow={ADMIN_ONLY}><AdminPage /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
